class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", (event) => {
      if (event.target.closest("price-range")) return;
      this.debouncedOnSubmit(event);
    });

    this.addEventListener("priceRangeUpdated", (event) => {
      this.onSubmitHandler(event);
    });
  }

  static setListeners() {
    window.addEventListener("popstate", (event) => {
      const searchParams = event.state?.searchParams || FacetFiltersForm.searchParamsInitial;
      if (searchParams !== FacetFiltersForm.searchParamsPrev)
        FacetFiltersForm.renderPage(searchParams, null, false);
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((el) => {
      el.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    document.getElementById("ProductGridContainer").classList.add("loading");

    sections.forEach(({ section }) => {
      const url = `${window.location.pathname}?section_id=${section}&${searchParams}`;
      const fromCache = FacetFiltersForm.filterData.find(fd => fd.url === url);

      fromCache
        ? FacetFiltersForm.renderSectionFromCache(fd => fd.url === url, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash)
      FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((r) => r.text())
      .then((html) => {
        FacetFiltersForm.filterData.push({ html, url });
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    document.getElementById("ProductGridContainer").innerHTML =
      parsed.getElementById("ProductGridContainer").innerHTML;
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const count = parsed.getElementById("ProductCount")?.innerHTML;
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");

    if (count) {
      container.innerHTML = count;
      container.classList.remove("loading");
      if (containerDesktop) {
        containerDesktop.innerHTML = count;
        containerDesktop.classList.remove("loading");
      }
    }
  }

  static renderFilters(html, event) {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const newFilters = parsed.querySelectorAll("#FacetFiltersForm .js-filter");

    newFilters.forEach((el) => {
      const current = document.getElementById(el.id);
      if (current) {
        current.innerHTML = el.innerHTML;
      }
    });

    FacetFiltersForm.renderActiveFacets(parsed);
  }

  static renderActiveFacets(parsed) {
    const active = parsed.querySelector(".active-facets");
    if (active) {
      document.querySelector(".active-facets").innerHTML = active.innerHTML;
    }
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, "", `${window.location.pathname}${searchParams ? `?${searchParams}` : ""}`);
  }

  static getSections() {
    return [{ section: document.getElementById("product-grid").dataset.id }];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll("facet-filters-form form");
    const params = new URLSearchParams();

    sortFilterForms.forEach((form) => {
      const formParams = new URLSearchParams(this.createSearchParams(form));
      for (const [key, value] of formParams.entries()) {
        if (!value) {
          params.delete(key);
        } else if (event.target.type === "checkbox") {
          if (event.target.name === key && event.target.value === value) {
            if (event.target.checked) {
              params.append(key, value);
            } else {
              params.delete(key);
            }
          } else {
            params.append(key, value);
          }
        } else {
          params.set(key, value);
        }
      }
    });

    const priceRangeEl = this.querySelector("price-range.facets__price");
    if (priceRangeEl) {
      const minInput = priceRangeEl.querySelector("input[name*='gte']");
      const maxInput = priceRangeEl.querySelector("input[name*='lte']");
      if (minInput?.value) params.set(minInput.name, minInput.value);
      if (maxInput?.value) params.set(maxInput.name, maxInput.value);
    }

    this.onSubmitForm(params.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const query = event.currentTarget.href.split("?")[1] || "";
    FacetFiltersForm.renderPage(query);
  }
}
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.debounceTimer = null;
    requestAnimationFrame(() => this.init());
  }

  init() {
    this.minInput = this.querySelector("input[name*='gte']");
    this.maxInput = this.querySelector("#Filter-Price-LTE");
    if (!this.minInput || !this.maxInput) return;

    this.loadPreviousValues();

    // Stop propagation so that the form’s 800ms debounce doesn’t fire too soon.
    this.minInput.addEventListener("input", (event) => {
      event.stopPropagation();
      this.handleInput(event);
    });
    this.maxInput.addEventListener("input", (event) => {
      event.stopPropagation();
      this.handleInput(event);
    });
    
    this.minInput.addEventListener("keyup", (event) => {
      event.stopPropagation();
      this.handleKeyUp(event);
    });
    this.maxInput.addEventListener("keyup", (event) => {
      event.stopPropagation();
      this.handleKeyUp(event);
    });
    
    this.minInput.addEventListener("blur", (event) => {
      event.stopPropagation();
      this.handleBlur(event);
    });
    this.maxInput.addEventListener("blur", (event) => {
      event.stopPropagation();
      this.handleBlur(event);
    });

    this.setMinAndMaxValues();
  }

  handleInput(event) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.onRangeChange(event);
    }, 800);
  }  

  handleKeyUp(event) {
    if (event.key === "Enter") {
      clearTimeout(this.debounceTimer);
      this.onRangeChange(event);
    }
  }

  handleBlur(event) {
    clearTimeout(this.debounceTimer);
    this.onRangeChange(event);
  }

  loadPreviousValues() {
    const urlParams = new URLSearchParams(window.location.search);
    let minValue = urlParams.get(this.minInput.name);
    let maxValue = urlParams.get(this.maxInput.name);

    this.minInput.value = minValue ? minValue : "";
    this.maxInput.value = maxValue ? maxValue : "";
    this.setMinAndMaxValues();
  }

  normalizeNumber(value) {
    return value.replace(/[, ]/g, '');
  }

  onRangeChange(event) {
    let minValue = this.minInput.value.trim();
    let maxValue = this.maxInput.value.trim();

    minValue = minValue ? parseFloat(this.normalizeNumber(minValue)) : null;
    maxValue = maxValue ? parseFloat(this.normalizeNumber(maxValue)) : null;

    // If the minimum is greater than maximum, clear the target field.
    if (minValue !== null && maxValue !== null && minValue > maxValue) {
      event.target.value = "";
      this.setMinAndMaxValues();
      return;
    }
    this.setMinAndMaxValues();

    this.dispatchEvent(new CustomEvent("priceRangeUpdated", { bubbles: true }));
  }

  setMinAndMaxValues() {
    let minValue = this.minInput.value.trim();
    let maxValue = this.maxInput.value.trim();

    minValue = minValue ? parseFloat(this.normalizeNumber(minValue)) : null;
    maxValue = maxValue ? parseFloat(this.normalizeNumber(maxValue)) : null;

    this.minInput.setAttribute("min", 0);
    if (maxValue !== null) {
      this.minInput.setAttribute("max", maxValue);
    } else {
      this.minInput.removeAttribute("max");
    }

    this.maxInput.setAttribute("min", minValue !== null ? minValue : 0);
    this.maxInput.removeAttribute("max");
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);
